"use client"

import React, {useEffect, useState} from "react";
import {BASE} from "@/services/Api";
import {AxiosError} from "axios";
import ErrorHandler from "@/components/utils/ErrorHandler";
import CloseButton from "@/components/tailwindComponents/button/CloseButton";
import ProductList from "@/components/product/ProductList";
import {useSearchParams, useRouter} from 'next/navigation';
import FiltersWrapper from "@/components/filters/Filters";
import InfiniteScroll from "react-infinite-scroll-component";
import Spinner from "@/components/tailwindComponents/Spinner";
import {GetTag} from "@/hooks/UseFetch";
import InputAsync from "@/components/input-fields/InputAsync";
import {ProductType} from "@/types/Product";
import {DEFAULT_PAGE_SIZE, PRODUCT_TYPE_CHOICES} from "@/constants";
import InputSelect from "@/components/input-fields/InputSelect";
import InputSwitch from "@/components/input-fields/InputSwitch";
import LPC from "@/components/landing/LPCCARD";
import SubmitButton from "@/components/tailwindComponents/button/SubmitButton";
import {toPersianDigits} from "@/components/utils/PersianNumber";
import LoadingDots from "@/components/utils/LoadingDots";

interface FiltersType {
    [key: string]: string | number | boolean | undefined;
}

const SEARCH_DEBOUNCE_MS = 500;
const SEARCH_MIN_LENGTH = 3;

const orderOptions = [
    {key: "-id", name: "جدیدترین"},
    {key: "id", name: "قدیمی‌ترین"},
    {key: "price", name: "ارزان‌ترین"},
    {key: "-price", name: "گران‌ترین"},
];

const Product = () => {
    const [data, setData] = useState<ProductType[]>([])
    const [page, setPage] = useState(1);
    const pageSize = DEFAULT_PAGE_SIZE
    const [loading, setLoading] = useState(true)
    const [order, setOrder] = useState("")
    const [filters, setFilters] = useState<FiltersType>({})
    const [error, setError] = useState<AxiosError | null>(null);
    const [count, setCount] = useState<number>(0);
    const [hasMore, setHasMore] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");

    // tag filters:
    const [selectedTag, setSelectedTag] = useState<{ label: string; value: string } | null>(null);
    // type filers:
    const [selectedType, setSelectedType] = useState<number | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<number | string>("");

    // special product filters:
    const [isSpecial, setIsSpecial] = useState<boolean>(false)

    const searchParams = useSearchParams();
    const router = useRouter();

    const [initialFiltersSet, setInitialFiltersSet] = useState(false);


    // url filters
    useEffect(() => {
        let updatedFilters = {...filters};
        if (searchParams.size > 0) {

            const hasSearch = searchParams.has('search');
            const hasTagId = searchParams.has('tagId');
            const hasTagName = searchParams.has('tagName');
            const hasType = searchParams.has('type');
            const special = searchParams.has('special');

            if (hasSearch) {
                const paramValue = searchParams.get('search') ?? "";
                updatedFilters.name = paramValue;
                setSearchText(paramValue);
            }

            if (hasType) {
                const typeValue = Number(searchParams.get('type')) ?? "";
                updatedFilters.type = typeValue;
                setSelectedType(typeValue);
            }

            if (hasTagId && hasTagName) {
                const tagId = searchParams.get('tagId') ?? "";
                updatedFilters.tag = tagId;
                setSelectedTag({value: tagId, label: searchParams.get('tagName') ?? ""});
            }

            if (special) {
                const specialValue = searchParams.get('special') ?? "";
                updatedFilters.special = specialValue;
                setIsSpecial(true)
            }

            setFilters(updatedFilters);

            const newParams = new URLSearchParams(searchParams.toString());
            if (hasSearch) newParams.delete('search');
            if (hasTagId) newParams.delete('tagId');
            if (hasTagName) newParams.delete('tagName');
            if (hasType) newParams.delete('type');
            if (special) newParams.delete('special');
            const newUrl = window.location.pathname + (newParams.toString() ? `?${newParams}` : '');
            router.replace(newUrl);
        }
        setInitialFiltersSet(true);
    }, [searchParams]);

    const convertFiltersToString = (filterObject: FiltersType): string => {
        const output: string[] = [];
        Object.entries(filterObject).forEach((filter) => {
            if (filter[1] || filter[1] === 0) {
                switch (filter[0]) {
                    case("name"):
                        output.push(`fulltext=${filter[1]}`)
                        break;
                    case("is_online"):
                        output.push(`filter_is_online=${filter[1]}`)
                        break;
                    case("tag"):
                        output.push(`filter_tags=${filter[1]}`)
                        break;
                    case("type"):
                        output.push(`filter_type=${filter[1]}`)
                        break;
                    case("status"):
                        output.push(`filter_status=${filter[1]}`)
                        break;
                    case("special"):
                        output.push(`filter_show_as_special=${filter[1]}`)
                        break;
                    default:
                        break;
                }
            }
        })
        return output.join('&');
    }

    const getData = async () => {
        setLoading(true)
        const filterQuery = convertFiltersToString(filters)

        try {
            const response = await BASE.PRODUCT.list(page, pageSize, filterQuery, order || "")
            const json = await response.data;

            if (json) {
                if (page === 1) {
                    setData(json.results);
                } else {
                    setData(prev => [...prev, ...json.results]);
                }

                setCount(json.count);
                setHasMore(json.count > page * pageSize);
            }
        } catch (e) {
            if (e instanceof AxiosError) {
                setError(e);
            } else {
                setError(null);
            }
            console.error("Unexpected error:", e);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!initialFiltersSet) return;
        getData();
    }, [page, order, filters]);

    // اگر order یا filter تغییر کنن، ریست کن
    useEffect(() => {
        setPage(1);
        setData([]);
        setHasMore(true);
    }, [filters, order]);

    // debounce جستجو: فقط وقتی کاربر SEARCH_DEBOUNCE_MS تایپ نکرد، فیلتر name آپدیت و ریکوئست زده می‌شود
    useEffect(() => {
        const timer = setTimeout(() => {
            const trimmed = searchText.trim();
            const value = trimmed.length >= SEARCH_MIN_LENGTH ? trimmed : "";

            setFilters((prevFilters) => {
                // اگر مقدار عوض نشده، state را تغییر نده تا ریکوئست اضافه زده نشود
                if (String(prevFilters.name ?? "") === value) return prevFilters;

                const updatedFilters = {...prevFilters};
                if (value) {
                    updatedFilters.name = value;
                } else {
                    delete updatedFilters.name;
                }
                return updatedFilters;
            });
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [searchText]);

    const loadMore = () => {
        if (!loading && hasMore) {
            setPage(prev => prev + 1);
        }
    }

    const handleTagChange = (tag: { label: string; value: string } | null) => {
        setSelectedTag(tag);

        if (!tag) {
            setFilters(prev => {
                const updated = {...prev};
                delete updated.tag;
                return updated;
            });
        } else {
            setFilters(prev => ({
                ...prev,
                tag: tag.value
            }));
        }
    };

    const handleSpecialProductSwitch = () => {
        setIsSpecial(!isSpecial)
        setFilters(prev => ({
            ...prev,
            special: !isSpecial
        }));
    };

    const handleTypeChange = (value: number) => {
        setSelectedType(value);

        if (!value) {
            setFilters(prev => {
                const updated = {...prev};
                delete updated.type;
                return updated;
            });
        } else {
            setFilters(prev => ({
                ...prev,
                type: value
            }));
        }
    };

    const handleStatusChange = (value: number) => {
        setSelectedStatus(value);

        if (!value) {
            setFilters(prev => {
                const updated = {...prev};
                delete updated.status;
                return updated;
            });
        } else {
            setFilters(prev => ({
                ...prev,
                status: value
            }));
        }
    };

    useEffect(() => {
        setFilters(prev => {
            const updated = {...prev};
            if (!selectedType || selectedType === null) {
                delete updated.type;
            } else {
                updated.type = selectedType;
            }
            return updated;
        });
    }, [selectedType]);

    const typeOptions = [
        {label: "دوره", value: PRODUCT_TYPE_CHOICES.COURSE},
        {label: "رویداد", value: PRODUCT_TYPE_CHOICES.EVENT},
    ];

    const statusOptions = [
        {label: "همه محصولات", value: ""},
        {label: "محصولات فعال", value: 1},
    ]

    const resetFilters = () => {
        setFilters({});
        setSearchText("");
        setSelectedTag({value: "", label: ""});
        setSelectedType(null);
        setIsSpecial(false);
        setSelectedStatus("");
    }

    return (
        <div className="container mx-auto py-12">
            <ErrorHandler error={error}/>
            <div className="flex w-full space-x-0 lg:space-x-4">

                {/* فیلترها */}
                <FiltersWrapper isOpen={isOpen} onClose={() => setIsOpen(false)}>
                    <div>
                        <input
                            type="text"
                            className="p-3 border rounded-lg w-full text-sm shadow-md border-gray-300"
                            placeholder="جستجو در محصولات..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                        {searchText.trim().length > 0 && searchText.trim().length < SEARCH_MIN_LENGTH && (
                            <p className="text-xs text-gray-500 mt-1">
                                برای جستجو حداقل {toPersianDigits(SEARCH_MIN_LENGTH)} کاراکتر وارد کنید
                            </p>
                        )}
                    </div>
                    <InputSelect
                        name="type"
                        label="نوع محصول"
                        options={typeOptions}
                        value={selectedType}
                        onChange={handleTypeChange}
                        error=""
                        firstOption=""
                    />
                    <div className="py-1">
                        <InputAsync
                            name="tag"
                            error=""
                            label="انتخاب برچسب"
                            loadData={GetTag}
                            multiSelect={false}
                            value={selectedTag}
                            onChange={handleTagChange}
                        />
                    </div>
                    <InputSelect
                        name="status"
                        label="وضعیت"
                        options={statusOptions}
                        value={selectedStatus}
                        onChange={handleStatusChange}
                        error=""
                        firstOption=""
                    />
                    <InputSwitch
                        name="special"
                        label="محصول ویژه"
                        error=""
                        value={isSpecial}
                        onChange={handleSpecialProductSwitch}
                    />

                    <div className="flex-grow"></div>
                    <div className="flex justify-center w-full pt-12">
                        <div className="absolute bottom-1">
                            <div className="inline-flex pb-2 space-x-2">
                                <div className="block lg:hidden">
                                    <SubmitButton
                                        text={loading ?
                                            <div className="py-1"><LoadingDots/>
                                            </div> : (count > 0 ? `مشاهده ${toPersianDigits(count)} محصول` : "محصول یافت نشد")}
                                        onClick={() => setIsOpen(false)}
                                        disabled={loading || count === 0}
                                        className={count === 0 ? "text-gray-500 border-gray-400" : ""}
                                    />
                                </div>
                                <CloseButton
                                    text="ریست"
                                    onClick={resetFilters}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>
                </FiltersWrapper>

                {/* لیست محصولات */}
                <div className="w-full lg:w-3/4">
                    <InfiniteScroll
                        dataLength={data.length}
                        next={loadMore}
                        hasMore={hasMore}
                        loader={<div className="flex w-full justify-center py-12"><Spinner className="size-12"/></div>}
                        className="custom-scroll-wrapper"
                    >
                        <ProductList<ProductType>
                            data={data}
                            count={count}
                            orderOptions={orderOptions}
                            setOrder={setOrder}
                            loading={loading}
                            setIsOpenFilters={setIsOpen}
                            initialSelected=""
                            initialSelectedName=""
                            renderItem={(item) => (
                                <LPC
                                    item={item}
                                    link="/product/"
                                    keyId={item.id}
                                    isProduct
                                />
                            )}
                        />
                    </InfiniteScroll>
                </div>
            </div>
        </div>
    )
}
export default Product;
